import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

export default {
    name: 'HeaderMegaMenu',
    setup() {
        const store = useStore()
        const root = ref(null)
        const menuToggle = ref(null)
        const openTab = ref(null)
        const responsiveMenuOpen = ref(false)
        const responsiveTab = ref(null)
        const responsiveCategory = ref(null)
        const motionSequenced = ref(false)
        let closeTimer = null
        let lastTrigger = null

        const focusFirst = async (selector) => {
            await nextTick()
            root.value?.querySelector(selector)?.focus({ preventScroll: true })
        }

        const cancelClose = () => {
            if (closeTimer) window.clearTimeout(closeTimer)
            closeTimer = null
        }

        const closeMenu = async (restoreFocus = false) => {
            cancelClose()
            const focusTarget = responsiveMenuOpen.value ? menuToggle.value : lastTrigger
            motionSequenced.value = false
            openTab.value = null
            responsiveMenuOpen.value = false
            responsiveTab.value = null
            responsiveCategory.value = null
            if (restoreFocus) {
                await nextTick()
                focusTarget?.focus()
            }
        }

        const scheduleClose = () => {
            cancelClose()
            closeTimer = window.setTimeout(() => {
                motionSequenced.value = false
                openTab.value = null
            }, 150)
        }

        const openMenu = (index, event) => {
            cancelClose()
            motionSequenced.value = openTab.value !== null && openTab.value !== index
            openTab.value = index
            if (event) lastTrigger = event.currentTarget
        }

        const focusDesktopPanel = (index) => {
            openTab.value = index
            focusFirst(`#m15-panel-${index} a, #m15-panel-${index} button`)
        }

        const toggleResponsiveMenu = async (event) => {
            if (responsiveMenuOpen.value) {
                await closeMenu(false)
                return
            }

            motionSequenced.value = false
            responsiveMenuOpen.value = true
            responsiveTab.value = null
            responsiveCategory.value = null
            event.currentTarget.blur()
            await focusFirst('[data-responsive-tab="0"]')
        }

        const selectResponsiveTab = async (index, event) => {
            const allowToggle = window.matchMedia('(min-width: 768px)').matches
            if (allowToggle && responsiveTab.value === index) {
                motionSequenced.value = false
                responsiveTab.value = null
                responsiveCategory.value = null
                event.currentTarget.focus({ preventScroll: true })
                return
            }

            motionSequenced.value = responsiveTab.value !== null && responsiveTab.value !== index
            responsiveTab.value = index
            responsiveCategory.value = null

            if (allowToggle) {
                await focusFirst(`#m15-responsive-tab-${index} .kb-header-mega-menu__responsive-category > button`)
                return
            }

            await focusFirst(`#m15-responsive-tab-${index} .kb-header-mega-menu__responsive-back`)
        }

        const clearResponsiveTab = async () => {
            motionSequenced.value = false
            const previousTab = responsiveTab.value
            responsiveTab.value = null
            responsiveCategory.value = null
            await focusFirst(`[data-responsive-tab="${previousTab}"]`)
        }

        const toggleCategory = (tabIndex, categoryIndex, event) => {
            const key = `${tabIndex}-${categoryIndex}`
            responsiveCategory.value = responsiveCategory.value === key ? null : key
            event.currentTarget.focus({ preventScroll: true })
        }

        const startSearchoverlay = (event) => {
            store.dispatch('main/openPageOverlay', {
                overlayContent: 'SearchOverlay',
                returnTarget: event.currentTarget
            })
        }

        const handleOutsideInteraction = (event) => {
            if (root.value && !root.value.contains(event.target)) closeMenu(false)
        }

        const handleBreakpointChange = () => closeMenu(false)

        onMounted(() => {
            document.addEventListener('pointerdown', handleOutsideInteraction)
            window.addEventListener('resize', handleBreakpointChange)
        })

        onBeforeUnmount(() => {
            document.removeEventListener('pointerdown', handleOutsideInteraction)
            window.removeEventListener('resize', handleBreakpointChange)
            cancelClose()
        })

        return {
            root,
            menuToggle,
            openTab,
            responsiveMenuOpen,
            responsiveTab,
            responsiveCategory,
            motionSequenced,
            openMenu,
            closeMenu,
            focusDesktopPanel,
            scheduleClose,
            cancelClose,
            toggleResponsiveMenu,
            selectResponsiveTab,
            clearResponsiveTab,
            toggleCategory,
            startSearchoverlay
        }
    }
}
